// FeatureRow — T-011. Three alternating image-and-text rows. Stacked on
// mobile; image and text alternate sides at sm+.

import type { JSX } from "react";
import { useI18n } from "../../i18n";

interface FeatureDef {
  id: "runner" | "tree" | "offline";
  image: { src: string; width: number; height: number; altKey: string };
  videoSrc?: string;
}

// T-098: the first feature is the workout runner (keypad, rest timer,
// target beside the field). It replaced the voice-control row when the
// human asked for the microphone to come out of the whole app.
const FEATURES: FeatureDef[] = [
  {
    id: "runner",
    image: {
      src: "/img/feat-muscleup.webp",
      width: 1376,
      height: 768,
      altKey: "landing.feature.runner.alt",
    },
    videoSrc: "/video/runner-demo.webm",
  },
  {
    id: "tree",
    image: {
      src: "/img/feat-planche.webp",
      width: 1376,
      height: 768,
      altKey: "landing.feature.tree.alt",
    },
  },
  {
    id: "offline",
    image: {
      src: "/img/feat-bridge.webp",
      width: 1376,
      height: 768,
      altKey: "landing.feature.offline.alt",
    },
  },
];

export function FeatureRow(): JSX.Element {
  const { t, locale } = useI18n();

  return (
    <section
      aria-label="features"
      className="border-y border-[var(--color-line)] bg-[var(--color-void)]"
    >
      <ul className="mx-auto flex w-full max-w-[1280px] flex-col px-0 sm:px-10">
        {FEATURES.map((feat, idx) => {
          const reverse = idx % 2 === 1;
          const alt = t(`landing.feature.${feat.id}.alt`);
          void locale;
          return (
            <li
              key={feat.id}
              className={
                "grid w-full grid-cols-1 items-stretch border-b border-[var(--color-line)] last:border-b-0 lg:grid-cols-2 " +
                (reverse ? "lg:[&>*:first-child]:order-2" : "")
              }
              style={{ minHeight: "320px" }}
            >
              <div className="relative aspect-[16/9] w-full overflow-hidden bg-[var(--color-slab)] lg:aspect-auto lg:h-full">
                {feat.videoSrc ? (
                  <video
                    src={feat.videoSrc}
                    poster={feat.image.src}
                    autoPlay
                    muted
                    loop
                    playsInline
                    aria-label={alt}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                ) : (
                  <img
                    src={feat.image.src}
                    alt={alt}
                    width={feat.image.width}
                    height={feat.image.height}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                )}
              </div>
              <div className="flex items-center bg-[var(--color-slab)] p-8 lg:p-12">
                <div className="max-w-prose">
                  <h3 className="font-display text-3xl uppercase leading-[1.1] tracking-[0.02em] text-[var(--color-ink-hi)] sm:text-4xl">
                    {t(`landing.feature.${feat.id}.title`)}
                  </h3>
                  <p
                    className="mt-4 font-sans text-base text-[var(--color-ink)]"
                    style={{ lineHeight: 1.5 }}
                  >
                    {t(`landing.feature.${feat.id}.body`)}
                  </p>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
